import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {I18NService} from '../../i18n/i18n.service';
import {SmagApiService} from '../../api/smag-api.service';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {BehaviorSubject, merge, Observable, of, Subject} from 'rxjs';
import {EC_DOCUMENT_METADATA_OPTIONAL, EC_DOCUMENT_PP, EntityType} from 'smag-commons/cfg/hierarchy';
import {catchError, debounceTime, filter, map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {DialogService} from '../../shared/dialog/dialog.service';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {MsgBoxService} from '../../shared/msg-box.service';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../shared/i18n-mat-paginator-intl';
import {SpinnerData} from '../../shared/spinner/spinner.component';
import {downloadBlob, errorCode, isDefined} from '../../commons/commons';
import {EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP, ECDocument} from 'smag-commons/cfg/hierarchy/ec';
import {DOCUMENT_TYPE_PATTERN} from '../../shared/patterns';
import {can} from '../../shared/permissions';
import {DocumentConfig, Permission} from 'smag-commons/dto/api';
import {ErrorCode} from 'src/app/api/errors';
import {EC_DOCUMENT_TYPE_OTHER} from '../ec-members/new-member/new-ec-member.component';
import {NAME_MAX_CHARS} from 'src/app/shared/limits';
import {mkTextField} from '../../shared/energy-community';

interface Documents {
    documents: ECDocument[];
    total: number;
}

interface MimeType {
    name: string;
    type: string;
}

interface Doc {
    filename: string;
    file: File;
}

enum SortCriterion {
    UPLOADED_AT = 'uploaded_at'
}

enum SortDirection {
    ASC = 'asc',
    DESC = 'desc'
}

const DEFAULT_SORT_CRITERIA: [SortCriterion, SortDirection][] = [
    [SortCriterion.UPLOADED_AT, SortDirection.DESC]
];

const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];

// const EC_DOCUMENT_METADATA_TO_BE_COMPILED = 'to_be_compiled';
const TAGS = [
    {
        name: 'All tags',
        value: null
    },
    {
        name: 'Visible in app',
        value: EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP
    }
    // 'To be compiled'
];

const PAGE_SIZE = 15;
const PAGE_SIZES = [15, 30, 50];
const DIALOG_WIDTH = 1200;
const SEARCH_DEBOUNCE_MILLIS = 250;
const MAX_FILE_MB = 2;

@Component({
    selector: 'app-ec-weather-forecast',
    templateUrl: './ec-documents.component.html',
    styleUrls: ['./ec-documents.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Documents per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECDocumentsComponent implements OnInit, OnDestroy, AfterViewInit {

    private destroyed = new Subject<void>();
    private reloadDocuments: Subject<void>;
    private searchForm: FormGroup;
    private sortCriteria: [SortCriterion, SortDirection][];
    @ViewChildren('paginator', {emitDistinctChangesOnly: true}) private paginator: QueryList<MatPaginator>;
    @ViewChild('newDocument') private newDocumentDialog: TemplateRef<any>;
    @ViewChild('editDocument') private editDocumentDialog: TemplateRef<any>;
    @ViewChild('deleteDocument') private deleteDocumentDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;
    readonly ACCEPTED_MIME_TYPES = FILE_MIME_TYPES.map(mt => mt.type).join(',');
    readonly EC_DOCUMENT_TYPE_OTHER = EC_DOCUMENT_TYPE_OTHER;

    ecResource: HierarchyEntity;
    displayedColumns: string[];
    documents: BehaviorSubject<Documents>;
    uploadDocForm: FormGroup;
    updateDocForm: FormGroup;
    currentDocument: Doc;
    updateDocument: Doc;
    documentTypes: string[];

    // exported in HTML
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;
    NAME_MAX_CHARS = NAME_MAX_CHARS;
    MAX_FILE_MB = MAX_FILE_MB;
    TAGS = TAGS;
    SortCriterion = SortCriterion;
    SortDirection = SortDirection;

    constructor(private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly router: Router,
                private readonly dialog: MatDialog,
                private readonly dialogService: DialogService,
                private readonly fb: FormBuilder) {
        this.reloadDocuments = new Subject<void>();
        this.displayedColumns = ['type', 'filename', 'uploadedOn', 'tag', 'actions'];
        this.documents = new BehaviorSubject<Documents>(null);
        this.sortCriteria = [...DEFAULT_SORT_CRITERIA];
        this.searchForm = fb.group({
            tagFilter: fb.control(TAGS[0], []),
            textFilter: fb.control('')
        });
        this.uploadDocForm = this.fb.group({
            filename: new FormControl(null, [Validators.required]),
            documentType: new FormControl(null, [Validators.required]),
            customDocumentType: new FormControl(null, [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
            visibleInApp: new FormControl(null, []),
            optional: new FormControl(false, [])
        });
        this.updateDocForm = this.fb.group({
            filename: new FormControl(null, [Validators.required]),
            documentId: new FormControl(null, []),
            documentType: new FormControl(null, [Validators.required]),
            customDocumentType: new FormControl(null, [Validators.pattern(DOCUMENT_TYPE_PATTERN)]),
            visibleInApp: new FormControl(null, []),
            optional: new FormControl(false, [])
        });
        this.currentDocument = null;
        this.updateDocument = null;
        this.ecResource = null;
        this.documentTypes = [];
    }

    ngOnInit() {
        this.hierarchyService.selection.pipe(
            map(resource => resource?.type === EntityType.ENERGY_COMMUNITY &&
            can(resource?.permissions, Permission.READ) ? resource : null),
            takeUntil(this.destroyed)
        ).subscribe(ecResource => {
            this.ecResource = ecResource || this.ecResource;
            if (this.ecResource) {
                this.reloadDocuments.next();  // reload docs now that we have the community id
            }
        });

        this.apiService.getCustomerConfig()
            .subscribe(customerConfig => {
                this.documentTypes = customerConfig !== null && customerConfig.ec_documents ?
                    [EC_DOCUMENT_TYPE_OTHER, ...customerConfig.ec_documents.map((document: DocumentConfig) => document.name)] :
                    [EC_DOCUMENT_TYPE_OTHER];
            });

        merge(this.searchForm.controls.textFilter.valueChanges.pipe(
                debounceTime(SEARCH_DEBOUNCE_MILLIS)),
            this.searchForm.controls.tagFilter.valueChanges.pipe(
                debounceTime(SEARCH_DEBOUNCE_MILLIS)))
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => {
                this.reloadDocuments.next();
            });

        this.uploadDocForm.controls.documentType.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => this.uploadDocForm.controls.visibleInApp.setValue(value && value !== EC_DOCUMENT_TYPE_OTHER));

        this.updateDocForm.controls.documentType.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => this.updateDocForm.controls.visibleInApp.setValue(value && value !== EC_DOCUMENT_TYPE_OTHER));
    }

    ngAfterViewInit() {
        this.paginator.changes.pipe(
            map((query: QueryList<MatPaginator>) => query?.first),
            startWith(this.paginator.first),
            filter((pag: MatPaginator) => !!pag),
            switchMap(paginator => merge(paginator.page,
                this.reloadDocuments.pipe(
                    tap(() => paginator.firstPage()))).pipe(
                startWith(undefined as unknown),
                filter(() => !!this.ecResource?.id),
                switchMap(() => this.mkDocuments(paginator.pageIndex + 1, paginator.pageSize).pipe(
                    startWith(null as Documents), // triggers spinner
                    catchError(err => {
                        this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving documents'), err);
                        return of(null as Documents);
                    }))))),
            takeUntil(this.destroyed))
            .subscribe(documents => {
                this.documents.next(documents);
                this.uploadDocForm.setValidators((ctrl: AbstractControl) => this.validateDocumentType(ctrl, documents.documents));
            });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get searchTextFilter(): FormControl {
        return this.searchForm.controls.textFilter as FormControl;
    }

    get searchTagFilter(): FormControl {
        return this.searchForm.controls.tagFilter as FormControl;
    }

    get canCreateDoc(): boolean {
        return can(this.ecResource?.permissions, Permission.CREATE);
    }

    get canEditDoc(): boolean {
        return can(this.ecResource?.permissions, Permission.UPDATE);
    }

    get canDeleteDoc(): boolean {
        return can(this.ecResource?.permissions, Permission.DELETE);
    }

    get canDownloadDoc(): boolean {
        return can(this.ecResource?.permissions, Permission.READ);
    }

    get mimeTypesNames(): string {
        return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
    }

    get documentTypeIsEmpty(): boolean {
        return this.uploadDocForm.hasError('empty') && this.uploadDocForm.controls.customDocumentType.dirty ||
            this.updateDocForm.hasError('empty') && this.updateDocForm.controls.customDocumentType.dirty;
    }

    get duplicatePrivacyPolicy(): boolean {
        return this.uploadDocForm.hasError('duplicate_pp') || this.updateDocForm.hasError('duplicate_pp');
    }

    getSortDirection(criterion: SortCriterion): SortDirection {
        const sortCriterion = this.sortCriteria.find(c => c[0] === criterion);
        return sortCriterion ? sortCriterion[1] : null;
    }

    onClickSortBy(criterion: SortCriterion, direction: SortDirection) {
        // remove current matching criterion (regardless of its direction)
        // and add new criterion right on top of criteria list
        this.sortCriteria = [
            [criterion, direction],
            ...this.sortCriteria.filter(c => c[0] !== criterion)
        ];
        // reload docs
        this.reloadDocuments.next();
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    uploadDocument(event: Event) {
        const files = 'files';
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                this.uploadDocForm.controls.filename.setValue(file.name);
                this.currentDocument = {
                    filename: file.name,
                    file
                };
            }
        } else {
            this.uploadDocForm.controls.filename.setValue(null);
        }
    }

    uploadEditedDocument(event: Event) {
        const files = 'files';
        if (event.target[files] && event.target[files].length) {
            const file: File = event.target[files][0];
            if (this.validateFile(file)) {
                this.updateDocForm.controls.filename.setValue(file.name);
                this.updateDocument = {
                    filename: file.name,
                    file
                };
            }
        } else {
            this.updateDocForm.controls.filename.setValue(null);
        }
    }

    removeDocument() {
        this.currentDocument = null;
        this.uploadDocForm.controls.filename.setValue(null);
        this.uploadDocForm.controls.visibleInApp.setValue(null);
        this.uploadDocForm.controls.optional.setValue(false);
    }

    resetCurrentDocForm() {
        this.currentDocument = null;
        this.uploadDocForm.reset();
    }

    removeEditedDocument() {
        this.updateDocument = null;
        this.updateDocForm.controls.filename.setValue(null);
        this.updateDocForm.controls.visibleInApp.setValue(null);
        this.updateDocForm.controls.optional.setValue(false);
    }

    onClickNewDocument() {
        this.uploadDocForm.controls.filename.setValue(null);
        this.uploadDocForm.controls.documentType.setValue(null);
        this.uploadDocForm.controls.customDocumentType.setValue(null);
        this.uploadDocForm.controls.visibleInApp.setValue(false);
        this.uploadDocForm.controls.optional.setValue(false);
        this.dialog.open(this.newDocumentDialog, {
            maxWidth: DIALOG_WIDTH,
            minWidth: DIALOG_WIDTH,
            maxHeight: '95vh',
            hasBackdrop: true
        }).afterClosed()
            .subscribe((add: boolean) => {
                if (add) {
                    this.createDocument()
                        .then(() => {
                            this.msgBoxService.info(this.i18n.translate('Document added'));
                            this.resetCurrentDocForm();
                            this.reloadDocuments.next();
                        })
                        .catch(err => {
                            this.msgBoxService.error(this.i18n.translate(mkErrorMessage(err, 'Error during document upload')));
                            this.resetCurrentDocForm();
                        });
                } else {
                    this.resetCurrentDocForm();
                }
            });
    }

    onClickEditDocument(document: ECDocument) {
        if (document.document_type !== EC_DOCUMENT_PP) {
            this.updateDocForm.setValidators((ctrl: AbstractControl) => this.validateDocumentType(ctrl, this.documents.value.documents));
        } else {
            this.updateDocForm.clearValidators();
        }
        this.updateDocForm.controls.filename.setValue(document.filename);
        this.updateDocForm.controls.documentType.setValue(this.documentTypes.includes(document.document_type) ? document.document_type : EC_DOCUMENT_TYPE_OTHER);
        this.updateDocForm.controls.customDocumentType.setValue(this.documentTypes.includes(document.document_type) ? '' : document.document_type);
        this.updateDocForm.controls.documentId.setValue(document.id);
        this.updateDocForm.controls.visibleInApp.setValue(document.metadata?.hasOwnProperty(EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP));
        this.updateDocForm.controls.optional.setValue(document.metadata?.[EC_DOCUMENT_METADATA_OPTIONAL]  ?? false);
        this.updateDocument = {
            filename: this.updateDocForm.controls.filename.value,
            file: {} as File  // temp placeholder file, the real one is not needed
        };
        this.dialog.open(this.editDocumentDialog, {
            maxWidth: DIALOG_WIDTH,
            minWidth: DIALOG_WIDTH,
            maxHeight: '95vh',
            hasBackdrop: true
        }).afterClosed()
            .subscribe((add: boolean) => {
                if (add) {
                    this.editDocument()
                        .then(() => {
                            this.msgBoxService.info(this.i18n.translate('Document updated'));
                            this.removeEditedDocument();
                            this.reloadDocuments.next();
                        })
                        .catch(err => {
                            this.msgBoxService.error(this.i18n.translate(mkErrorMessage(err, 'Error during document update')));
                            this.removeEditedDocument();
                        });
                } else {
                    this.removeEditedDocument();
                }
            });
    }

    onClickDeleteDocument(doc: ECDocument) {
        this.dialogService.open<any, boolean>({
            title: this.i18n.translate('Delete document'),
            template: this.deleteDocumentDialog,
            context: {
                doc
            }
        }).closed.then(del => {
            if (del) {
                this.deleteDocument(doc.id);
            }
        });
    }

    onClickDownloadCommunityDocument(doc: ECDocument) {
        this.apiService.getECDocument(this.ecResource.id, doc.id)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
            });
    }

    onClickGotoDashboard() {
        this.router.navigate(['/c', 'ec', 'dashboard'], {
            queryParamsHandling: 'preserve'
        }).then();
    }

    resetTagsFilter() {
        this.searchTagFilter.setValue(TAGS[0]);
    }

    getDocumentTag(doc: ECDocument): string {
        if (doc.metadata?.hasOwnProperty(EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP)) {
            return 'visible-in-app';  // TODO -- fare una mappa sensata per questa cosa
        }
        return 'none';
    }

    getTagName(tagName: string): string {
        switch (tagName) {
            case 'visible-in-app':
                return 'Visibile in app';
            default:
                return 'No tag';
        }
    }

    get mimeTypes(): string[] {
        return FILE_MIME_TYPES.map(mt => mt.type);
    }

    private validateFile(file: File): boolean {
        if (file.size > MAX_FILE_MB * 1048576) {
            this.msgBoxService.error(this.i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
            return false;
        }
        if (!this.mimeTypes.includes(file.type)) {
            this.msgBoxService.error(this.i18n.translate('Accepts only {0}', this.mimeTypesNames));
            return false;
        }
        return true;
    }

    private createDocument(): Promise<ECDocument | void> {
        const fileName: string = this.uploadDocForm.controls.filename.value;
        const type: string = this.uploadDocForm.controls.documentType.value === EC_DOCUMENT_TYPE_OTHER ?
            this.uploadDocForm.controls.customDocumentType.value :
            this.uploadDocForm.controls.documentType.value;
        const file = {filename: fileName, content: this.currentDocument.file};
        let metadata = null;
        if (this.uploadDocForm.controls.visibleInApp.value) {
            metadata = {[EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP]: true};
        }
        metadata = {...metadata, [EC_DOCUMENT_METADATA_OPTIONAL]: this.uploadDocForm.controls.optional.value ?? false};
        return this.apiService.createECDocument(this.ecResource.id, file, type, metadata);
    }

    private editDocument(): Promise<ECDocument | void> {
        const fileName: string = this.updateDocForm.controls.filename.value;
        const type: string = this.updateDocForm.controls.documentType.value === EC_DOCUMENT_TYPE_OTHER ?
            this.updateDocForm.controls.customDocumentType.value :
            this.updateDocForm.controls.documentType.value;
        const docId: string = this.updateDocForm.controls.documentId.value;
        let metadata = null;
        if (this.updateDocForm.controls.visibleInApp.value) {
            metadata = {[EC_DOCUMENT_METADATA_VISIBLE_IN_MAPP]: true};
        }
        metadata = {...metadata, [EC_DOCUMENT_METADATA_OPTIONAL]: this.updateDocForm.controls.optional.value ?? false};
        // We only send file if it has been updated
        const file = !!this.updateDocument.file?.name ? {filename: fileName, content: this.updateDocument.file} : null;
        return this.apiService.updateECDocument(this.ecResource.id, docId, type, file, metadata);
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }

    private deleteDocument(docId: string) {
        this.apiService.deleteECDocument(this.ecResource.id, docId)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Document removed'));
                this.reloadDocuments.next();
            })
            .catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while deleting the document'), err));
    }


    private mkDocuments(page: number, size: number): Observable<Documents> {
        // filtering conditions
        const textFilter: string = this.searchTextFilter.value?.trim() || null;
        const mobileVisible = !!(this.searchTagFilter.value as { name: string, value: string }).value;
        // sorting criteria
        const orderBy: string[] = this.sortCriteria.map(([criterion, direction]) => `${criterion}_${direction}`);
        return this.apiService.listECDocuments(page, size,
            this.ecResource.id,  // filter for current community
            textFilter,  // filter for document type
            mobileVisible,  // filter by visibleInApp
            orderBy).pipe(map(a => ({documents: a.results, total: a.total} as Documents)));
    }

    private validateDocumentType(ctrl: AbstractControl, documents: ECDocument[]): ValidationErrors | null {
        const errs: ValidationErrors = {};
        const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
        // custom type only mandatory for `OTHER` type documents
        if ((ctrls.documentType.value === EC_DOCUMENT_TYPE_OTHER)
            && !mkTextField(ctrls.customDocumentType.value)) {
            errs.empty = true;
        } else {
            errs.empty = null;
        }
        // only one privacy policy document for one ec can be uploaded
        const documentTypes = documents.map(doc => doc.document_type);
        if (ctrls.documentType.value === EC_DOCUMENT_PP && documentTypes.includes(ctrls.documentType.value) ||
            ctrls.customDocumentType.value === EC_DOCUMENT_PP && documentTypes.includes(ctrls.customDocumentType.value)) {
            errs.duplicate_pp = true;
        } else {
            errs.duplicate_pp = null;
        }
        return Object.values(errs).every(err => !err) ? null : errs;
    }
}

function mkErrorMessage(err: Error, fallback: string): string {
    const code = errorCode(err);
    switch (code) {
        case ErrorCode.INVALID_EC_DOCUMENT_ID:
            return 'Invalid energy community document ID';
        case ErrorCode.CANNOT_CREATE_EC_DOCUMENT:
            return 'An error occurred while creating document for EC';
        case ErrorCode.CANNOT_UPDATE_EC_DOCUMENT:
            return 'An error occurred while updating document of EC';
        case ErrorCode.CANNOT_DELETE_EC_DOCUMENT:
            return 'An error occurred while deleting document of EC';
        case ErrorCode.CANNOT_RETRIEVE_EC_DOCUMENT:
            return 'An error occurred while retrieving document of EC';
        case ErrorCode.CANNOT_RETRIEVE_EC_DOCUMENTS:
            return 'An error occurred while retrieving documents for EC';
        default:
            return fallback;
    }
}



